/**
 * Compose modal for the global Messages view (#542 slice 2) — start a new
 * firm-facing thread through the CC worker's compose proxies.
 *
 * Two modes (segmented toggle, the Surface pill pattern):
 *   - "About a case" — the portal's per-case door keys on a PLACEMENT + side.
 *     The operator finds the matter (the global-search API, Enter-to-search —
 *     same posture as the top strip), then addresses a side: OF, or the HF on
 *     a routing leg (the legs come from the compose-targets read; a matter
 *     with one leg skips the leg picker). Recipients are the side firm's
 *     default lead-recipient contacts (recipient preview is a named
 *     follow-up, not built).
 *   - "To a firm contact" — the portal's contact door addresses ONE contact.
 *     Firm picker → that firm's Active contacts (from the Client Success
 *     users roster). Visibility "Firm" (everyone at the firm sees the thread)
 *     unless the operator checks Direct.
 *
 * Copy states the blast radius plainly — a compose emails real firm people
 * and posts to Slack; the button says what it does ("Send message").
 */

import { useRef, useState } from "react";
import {
  MESSAGE_BODY_MAX,
  MESSAGE_SUBJECT_MAX,
  type SearchResponse,
  type SearchResult,
} from "@command-center/shared";
import { apiGet } from "@/lib/api";
import {
  replySavedOf,
  useCaseComposeTargets,
  useComposeCaseMessage,
  useComposeFirmMessage,
} from "@/lib/use-firm-messages";
import { useClientSuccessUsers } from "@/lib/use-client-success-users";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { SegmentedTab } from "@/components/ui/segmented-tab";
import { Textarea } from "@/components/ui/textarea";

type Mode = "case" | "firm";

export function ComposeMessageModal({
  open,
  onClose,
  testId = "compose-message",
}: {
  open: boolean;
  onClose: () => void;
  testId?: string;
}) {
  // Remount the form state on every open so a sent/abandoned draft never
  // leaks into the next compose.
  if (!open) return null;
  return <ComposeForm onClose={onClose} testId={testId} />;
}

function ComposeForm({ onClose, testId }: { onClose: () => void; testId: string }) {
  const [mode, setMode] = useState<Mode>("case");

  // --- case mode state ---
  const [caseQuery, setCaseQuery] = useState("");
  const [caseResults, setCaseResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [pickedCase, setPickedCase] = useState<SearchResult | null>(null);
  const [side, setSide] = useState<"OF" | "HF">("HF");
  const [placementId, setPlacementId] = useState<string | null>(null);
  const targets = useCaseComposeTargets(pickedCase?.id ?? null);

  // --- firm mode state ---
  const users = useClientSuccessUsers();
  const [firmId, setFirmId] = useState("");
  const [contactId, setContactId] = useState("");
  const [direct, setDirect] = useState(false);

  // --- shared ---
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const composeCase = useComposeCaseMessage();
  const composeFirm = useComposeFirmMessage();
  const pending = composeCase.isPending || composeFirm.isPending;
  const failed = composeCase.isError || composeFirm.isError;

  // Monotonic search sequence — a re-search supersedes any in-flight request,
  // so a slow earlier response can never overwrite newer results (#595 review).
  const searchSeq = useRef(0);
  async function runCaseSearch() {
    const q = caseQuery.trim();
    if (q === "") return;
    const seq = ++searchSeq.current;
    setSearching(true);
    try {
      const resp = await apiGet<SearchResponse>(`/api/search?q=${encodeURIComponent(q)}`);
      if (seq !== searchSeq.current) return; // stale — a newer search took over
      setCaseResults(resp.results);
    } catch {
      if (seq === searchSeq.current) setCaseResults([]);
    } finally {
      if (seq === searchSeq.current) setSearching(false);
    }
  }

  // Switching modes starts a fresh attempt — clear the other mode's failure
  // so its failure line doesn't read as this mode's (#595 review).
  function switchMode(next: Mode) {
    setMode(next);
    composeCase.reset();
    composeFirm.reset();
  }

  // Messageable contacts: Active with an email on file — the portal door
  // rejects anything else, so don't offer it.
  const contacts = (users.data?.users ?? []).filter(
    (u) => u.status === "Active" && u.email && u.firmId,
  );
  const firms = [...new Map(contacts.map((u) => [u.firmId!, u.firmName ?? u.firmId!]))]
    .sort((a, b) => a[1].localeCompare(b[1]));
  const firmContacts = contacts.filter((u) => u.firmId === firmId);

  // The leg being addressed. One leg → it's the pick; several → the operator
  // chose one (HF side only; the OF resolves via the lead, so any leg works).
  const legs = targets.data?.targets ?? [];
  const activePlacementId =
    side === "OF"
      ? (legs[0]?.placementId ?? null)
      : legs.length === 1
        ? legs[0]!.placementId
        : placementId;

  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();
  const contentReady =
    trimmedSubject !== "" &&
    trimmedSubject.length <= MESSAGE_SUBJECT_MAX &&
    trimmedBody !== "" &&
    trimmedBody.length <= MESSAGE_BODY_MAX;
  const targetReady =
    mode === "case" ? activePlacementId !== null : contactId !== "";
  const canSend = contentReady && targetReady && !pending;

  function send() {
    if (!canSend) return;
    if (mode === "case") {
      composeCase.mutate(
        {
          placementId: activePlacementId!,
          side,
          subject: trimmedSubject,
          body: trimmedBody,
        },
        { onSuccess: onClose },
      );
    } else {
      composeFirm.mutate(
        {
          contactId,
          visibility: direct ? "Direct" : "Firm",
          subject: trimmedSubject,
          body: trimmedBody,
        },
        { onSuccess: onClose },
      );
    }
  }

  return (
    <Dialog open onClose={onClose} labelledBy={`${testId}-title`} testId={testId}>
      <DialogHeader>
        <DialogTitle id={`${testId}-title`}>New message</DialogTitle>
        <DialogDescription>
          Emails the firm and posts to Slack. Replies land back in this thread.
        </DialogDescription>
      </DialogHeader>

      <div className="mb-4 flex w-fit overflow-hidden rounded-md border" role="tablist">
        <SegmentedTab
          active={mode === "case"}
          onClick={() => switchMode("case")}
          label="About a case"
          testId={`${testId}-mode-case`}
        />
        <SegmentedTab
          active={mode === "firm"}
          onClick={() => switchMode("firm")}
          label="To a firm contact"
          testId={`${testId}-mode-firm`}
        />
      </div>

      <div className="flex flex-col gap-4">
        {mode === "case" ? (
          <div className="flex flex-col gap-3">
            {pickedCase === null ? (
              <>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">Find the case</span>
                  <Input
                    value={caseQuery}
                    onChange={(e) => setCaseQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void runCaseSearch();
                    }}
                    placeholder="Client name — press Enter to search"
                    data-testid={`${testId}-case-query`}
                  />
                </label>
                {searching ? (
                  <p className="text-sm text-muted-foreground">Searching…</p>
                ) : null}
                {caseResults !== null && !searching ? (
                  caseResults.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No matters match. Try the client's surname.
                    </p>
                  ) : (
                    <ul className="flex max-h-48 flex-col gap-0.5 overflow-y-auto">
                      {caseResults.map((r) => (
                        <li key={r.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setPickedCase(r);
                              setPlacementId(null);
                            }}
                            data-testid={`${testId}-case-result`}
                            className="flex w-full flex-col rounded px-2 py-1.5 text-left hover:bg-accent"
                          >
                            <span className="text-sm">{r.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {[r.status, r.originatingFirmName].filter(Boolean).join(" · ")}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )
                ) : null}
              </>
            ) : (
              <>
                <div className="flex items-center justify-between rounded border px-2 py-1.5">
                  <span className="text-sm" data-testid={`${testId}-picked-case`}>
                    {pickedCase.name}
                    {pickedCase.originatingFirmName ? (
                      <span className="text-muted-foreground">
                        {" "}
                        · {pickedCase.originatingFirmName}
                      </span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setPickedCase(null);
                      setCaseResults(null);
                      setPlacementId(null);
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                    data-testid={`${testId}-change-case`}
                  >
                    Change
                  </button>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">Message the</span>
                  <div className="flex w-fit overflow-hidden rounded-md border" role="tablist">
                    <SegmentedTab
                      active={side === "HF"}
                      onClick={() => setSide("HF")}
                      label="Handling firm"
                      testId={`${testId}-side-hf`}
                    />
                    <SegmentedTab
                      active={side === "OF"}
                      onClick={() => setSide("OF")}
                      label="Originating firm"
                      testId={`${testId}-side-of`}
                    />
                  </div>
                </div>

                {targets.isLoading ? (
                  <p className="text-sm text-muted-foreground">Loading placements…</p>
                ) : legs.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid={`${testId}-no-legs`}>
                    This matter has no placements yet, so there's no firm to
                    message. Route it first.
                  </p>
                ) : side === "HF" && legs.length > 1 ? (
                  <fieldset className="flex flex-col gap-1">
                    <legend className="pb-1 text-sm font-medium">
                      Which handling firm?
                    </legend>
                    {legs.map((leg) => (
                      <label
                        key={leg.placementId}
                        className="flex items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-accent"
                      >
                        <input
                          type="radio"
                          name={`${testId}-leg`}
                          checked={placementId === leg.placementId}
                          onChange={() => setPlacementId(leg.placementId)}
                          data-testid={`${testId}-leg`}
                        />
                        <span>{leg.hfFirmName ?? "Unknown firm"}</span>
                        {leg.decision ? (
                          <span className="text-xs text-muted-foreground">{leg.decision}</span>
                        ) : null}
                      </label>
                    ))}
                  </fieldset>
                ) : null}
              </>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {users.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading contacts…</p>
            ) : (
              <>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">Firm</span>
                  <NativeSelect
                    value={firmId}
                    onChange={(e) => {
                      setFirmId(e.target.value);
                      setContactId("");
                    }}
                    data-testid={`${testId}-firm-select`}
                  >
                    <option value="">Choose a firm…</option>
                    {firms.map(([id, name]) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </NativeSelect>
                </label>
                {firmId !== "" ? (
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Contact</span>
                    <NativeSelect
                      value={contactId}
                      onChange={(e) => setContactId(e.target.value)}
                      data-testid={`${testId}-contact-select`}
                    >
                      <option value="">Choose a contact…</option>
                      {firmContacts.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name || u.email}
                        </option>
                      ))}
                    </NativeSelect>
                  </label>
                ) : null}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={direct}
                    onChange={(e) => setDirect(e.target.checked)}
                    data-testid={`${testId}-direct`}
                  />
                  <span>
                    Direct — only this contact sees it (otherwise everyone at
                    the firm does)
                  </span>
                </label>
              </>
            )}
          </div>
        )}

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">Subject</span>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={MESSAGE_SUBJECT_MAX}
            data-testid={`${testId}-subject`}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">Message</span>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            maxLength={MESSAGE_BODY_MAX}
            data-testid={`${testId}-body`}
          />
        </label>
        {failed ? (
          <p className="text-sm text-error" data-testid={`${testId}-error`}>
            {replySavedOf(composeCase.error ?? composeFirm.error) === false
              ? "Nothing was saved; it can be sent again."
              : "It may have saved. Read the Firm's threads before sending it again."}
          </p>
        ) : null}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button onClick={send} disabled={!canSend} data-testid={`${testId}-send`}>
          {pending ? "Sending…" : "Send message"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
